import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'warning'];
  static values = {
    checkUrl: String,
    organizationId: String,
  };

  connect() {
    this.emailConfirmed = false;
    this.needsConfirmation = false;

    // Bind the handler so we can remove it later
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);

    // Intercept form submission
    this.form = this.element.closest('form');
    if (this.form) {
      this.form.addEventListener('submit', this.boundHandleFormSubmit);
    }
  }

  disconnect() {
    // Clean up event listener
    if (this.form && this.boundHandleFormSubmit) {
      this.form.removeEventListener('submit', this.boundHandleFormSubmit);
    }
  }

  async checkEmail() {
    const email = this.inputTarget.value.trim();

    if (!email || !this.isValidEmail(email)) {
      this.hideWarning();
      this.needsConfirmation = false;
      this.emailConfirmed = false;
      return;
    }

    try {
      const response = await fetch(`${this.checkUrlValue}?email=${encodeURIComponent(email)}`);
      const data = await response.json();

      if (data.same_org && data.is_employee) {
        // Staff member in org - show confirmation, they may intentionally add themselves as guardian
        this.showWarning();
        this.needsConfirmation = true;
        this.emailConfirmed = false;
      } else {
        // Either no user, or it's a duplicate guardian (will be blocked by model)
        this.hideWarning();
        this.needsConfirmation = false;
        this.emailConfirmed = true;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error checking email:', error);
      // On error, allow form to proceed (fail open)
      this.hideWarning();
      this.needsConfirmation = false;
      this.emailConfirmed = true;
    }
  }

  showWarning() {
    const message =
      'This email belongs to an existing user who is a staff member in your organization. Do you want to proceed?';
    this.warningTarget.querySelector('.confirmation-message').textContent = message;
    this.warningTarget.hidden = false;
  }

  hideWarning() {
    this.warningTarget.hidden = true;
  }

  confirmProceed(event) {
    event.preventDefault();
    this.emailConfirmed = true;
    this.needsConfirmation = false;
    this.hideWarning();

    // User can now continue editing the form
  }

  cancelProceed(event) {
    event.preventDefault();
    // Clear the email field and hide warning
    this.inputTarget.value = '';
    this.hideWarning();
    this.needsConfirmation = false;
    this.emailConfirmed = false;
    this.inputTarget.focus();
  }

  handleFormSubmit(event) {
    // If email needs confirmation but hasn't been confirmed yet, block submission
    if (this.needsConfirmation && !this.emailConfirmed) {
      event.preventDefault();
      event.stopPropagation();

      // Show the warning again if it's hidden
      if (this.warningTarget.hidden) {
        this.warningTarget.hidden = false;
      }

      // Scroll to the warning
      this.warningTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return false;
    }
    return true;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
